/**
 * Mobile UPI Intent helpers (PayU S2S, see functions/src/payuUpiCore.ts).
 *  - Desktop: PayU's full hosted page (UPI QR / cards / netbanking).
 *  - Mobile (15 Oct 2026): "hosted_upi" = the same hosted page posted with pg=UPI, bankcode=INTENT, which
 *    lands directly on PayU's "Pay using any UPI app" grid (GPay / PhonePe / Paytm …), not the UPI-ID field
 *    (UPI Collect is dead since NPCI's Feb 2026 mandate). Cards / netbanking stay one tap away ("hosted").
 *    The direct S2S UPI Intent never completed a live payment (29 Sep 2026: txn dropped by PayU),
 *    so it is opt-in until PayU confirms the intent setup for our MID.
 *  - settings/global.payuUpiIntent: "default" -> mobile opens the UPI app directly (old behaviour),
 *    "beta" -> hosted page first + a secondary "Pay directly in UPI app (beta)" link, else hosted only.
 * Debug override: localStorage.payuFlow = "hosted" | "upi_intent".
 */
export type PayPlatform = "android" | "ios" | "mobile_other" | "desktop";
export type PayFlow = "hosted" | "hosted_upi" | "upi_intent";

export interface UpiIntentLinks {
  upi: string;
  androidIntent: string;
  ios: Record<string, string>;
  merchantName?: string | null;
  merchantVpa?: string | null;
}

export function detectPayPlatform(
  ua: string = typeof navigator !== "undefined" ? navigator.userAgent || "" : "",
  maxTouchPoints: number = typeof navigator !== "undefined" ? navigator.maxTouchPoints || 0 : 0,
  coarseOnly: boolean = typeof window !== "undefined" && !!window.matchMedia
    && window.matchMedia("(pointer: coarse)").matches && !window.matchMedia("(pointer: fine)").matches
): PayPlatform {
  // iPadOS 13+ reports a desktop Mac UA; touch points give it away.
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && maxTouchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android"; // includes the Ruchira TWA (Chrome Android UA)
  if (/Mobile|Windows Phone|Opera Mini|IEMobile/i.test(ua) || coarseOnly) return "mobile_other";
  return "desktop";
}

/** Remote switch (settings/global.payuUpiIntent) for the direct UPI-app flow on mobile. */
export type UpiIntentMode = "off" | "beta" | "default";
export function parseUpiIntentMode(v: unknown): UpiIntentMode {
  return v === "default" || v === "beta" ? v : "off";
}

export function preferredPayFlow(
  platform: PayPlatform = detectPayPlatform(),
  mode: UpiIntentMode = "off"
): PayFlow {
  try {
    const o = localStorage.getItem("payuFlow");
    if (o === "hosted" || o === "upi_intent" || o === "hosted_upi") return o;
  } catch { /* ignore */ }
  if (platform === "desktop") return "hosted";
  return mode === "default" ? "upi_intent" : "hosted_upi";
}

/** Show the secondary "Pay directly in UPI app" option (mobile only, when not already the default). */
export function showDirectUpiOption(platform: PayPlatform, mode: UpiIntentMode): boolean {
  if (platform === "desktop") return false;
  try {
    if (localStorage.getItem("payuFlow") === "upi_intent") return false; // already the primary flow
  } catch { /* ignore */ }
  return mode === "beta";
}

/** Only deep links we built server-side are ever opened. */
const SAFE_LINK = /^(upi|intent|tez|gpay|phonepe|paytmmp|bhim|credpay):\/\/[A-Za-z0-9._~%!$'()*+,;=:@/?&#-]+$/;
export const isSafeUpiLink = (l: unknown): l is string => typeof l === "string" && l.length < 2000 && SAFE_LINK.test(l);

export const IOS_UPI_APPS: { id: string; label: string }[] = [
  { id: "gpay", label: "Google Pay" },
  { id: "phonepe", label: "PhonePe" },
  { id: "paytm", label: "Paytm" },
  { id: "bhim", label: "BHIM" },
  { id: "cred", label: "CRED" },
];

/** Where the app lands after a confirmed payment (same URLs PayU's surl redirect uses). */
export function payuReturnPath(returnTo: string | undefined, purpose: string): string {
  const rt = returnTo || (purpose === "order_payment" ? "checkout" : "profile");
  return rt === "checkout" ? "/checkout" : rt === "wallet" ? "/customer/dashboard" : "/profile";
}

/**
 * Pending UPI intent, persisted so a payment still completes the checkout if the mobile browser
 * reloads the tab while the customer is in their UPI app (common on low-memory Android phones).
 */
const PENDING_LS = "payu_upi_pending";
export const PENDING_UPI_MAX_AGE_MS = 20 * 60 * 1000;
export interface PendingUpi { txnid: string; path: string; at: number }

export function savePendingUpi(p: PendingUpi): void {
  try { localStorage.setItem(PENDING_LS, JSON.stringify(p)); } catch { /* ignore */ }
}
export function loadPendingUpi(now = Date.now()): PendingUpi | null {
  try {
    const p = JSON.parse(localStorage.getItem(PENDING_LS) || "null");
    if (!p || typeof p.txnid !== "string" || !/^[A-Za-z0-9_-]{1,40}$/.test(p.txnid)) return null;
    if (typeof p.at !== "number" || now - p.at > PENDING_UPI_MAX_AGE_MS) { clearPendingUpi(); return null; }
    const path = ["/checkout", "/profile", "/customer/dashboard"].includes(p.path) ? p.path : "/profile";
    return { txnid: p.txnid, path, at: p.at };
  } catch { return null; }
}
export function clearPendingUpi(txnid?: string): void {
  try {
    if (txnid) {
      const p = JSON.parse(localStorage.getItem(PENDING_LS) || "null");
      if (p && p.txnid !== txnid) return;
    }
    localStorage.removeItem(PENDING_LS);
  } catch { /* ignore */ }
}

/** txnid the open AddMoneyModal is already watching (so the global watcher stays out of the way). */
export const upiModalWatching: { txnid: string | null } = { txnid: null };
